/**
 * Plain-English description of one recurring cost row for the Cost Entry
 * table (operator feedback 2026-10-15: "I can't tell what kind of utilities
 * (like electricity or wifi)" / "I can't even tell what Other is").
 *
 * The cost type alone ("Utilities", "Other") says too little, so we read the
 * row's notes for what it actually is and who supplies it. Pure functions,
 * no network. Unknown rows fall back to the cost type label.
 */

const KINDS = [
  // order matters: the first match wins
  { re: /elec(tricity)?\s*(\+|&|and)\s*gas/i, kind: 'Electricity and gas' },
  { re: /\bbroadband\b|\bwifi\b|\bwi-fi\b|\bfibre\b|\binternet\b/i, kind: 'Wifi' },
  { re: /\belectricit[yé]|\belec\b|\bEDF\b/i, kind: 'Electricity' },
  { re: /\bgas\b/i, kind: 'Gas' },
  { re: /\bwater\b|\beau\b/i, kind: 'Water' },
  { re: /\bTV licen[cs]e\b/i, kind: 'TV licence' },
  { re: /short[- ]term let licen[cs]e|\bSTL\b/i, kind: 'Short-term let licence' },
  { re: /\bparking\b/i, kind: 'Parking' },
  { re: /owner payout/i, kind: 'Owner payout' },
  { re: /\bsyndic\b|copropri/i, kind: 'Syndic' },
  { re: /council tax/i, kind: 'Council tax' },
  { re: /taxe fonci/i, kind: 'Taxe foncière' },
  { re: /insurance|assurance/i, kind: 'Insurance' },
  { re: /arrears/i, kind: 'Arrears instalment' },
  { re: /\bclean/i, kind: 'Cleaning' },
  { re: /renovation|materials/i, kind: 'Renovation materials' },
  { re: /mortgage|pr[eê]t|loan/i, kind: 'Mortgage' },
  { re: /\brent\b|loyer/i, kind: 'Rent' },
];

const SUPPLIERS = [
  'Octopus', 'Scottish Power', 'British Gas', 'Utility Warehouse', 'EDF', 'Engie',
  'Virgin Media', 'Sky', 'Free', 'Orange', 'SFR', 'Bouygues',
  'ABACUS', 'HCC International', 'NBS', 'AXA', 'Allianz',
  'YourParkingSpace', 'Trinity Factors', 'Immobiliere Convention', 'Milards',
  'Splendid Property', 'Blair Residential', 'Lewis Residential', 'Scotia Property',
  'BNP', 'Morisseau', 'NOVADB', 'BOSA',
];

/** What the cost is ("Wifi", "TV licence"...), or null when the notes say nothing. */
export function costKind(cost) {
  const notes = cost?.notes || '';
  for (const k of KINDS) if (k.re.test(notes)) return k.kind;
  return null;
}

/** Supplier / payee named in the notes, or null. */
export function costSupplier(cost) {
  const notes = cost?.notes || '';
  for (const s of SUPPLIERS) {
    const re = new RegExp(`\\b${s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (re.test(notes)) return s;
  }
  return null;
}

/**
 * Label for the row badge: the specific kind when the notes give one,
 * otherwise the cost-type label. `typeLabel` = taxonomy label for cost_type.
 */
// Only the catch-all types get renamed; specific types (rent, mortgage,
// council tax...) already say what they are.
const GENERIC_TYPES = new Set(['utilities', 'other', 'maintenance']);

export function costRowLabel(cost, typeLabel) {
  if (!GENERIC_TYPES.has(cost?.cost_type)) return typeLabel;
  return costKind(cost) || typeLabel;
}

/** Safe number for amounts stored as strings ("189.81"); never NaN. */
export function costAmount(v) {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

// Monthly cost of a property's live Cost Entry lines (Fan, 2026-10-30: "add a
// column after Cost types 'Monthly costs'"). Monthly lines count in full,
// quarterly / 3, annual / 12; per-stay cleaning and one-off lines are not a
// monthly cost. "Live" = started and not ended before this month began.
const PER_MONTH = { monthly: 1, quarterly: 1 / 3, annual: 1 / 12, yearly: 1 / 12 };

export function monthlyCostsByCurrency(costs, todayIso) {
  const today = todayIso || new Date().toISOString().slice(0, 10);
  const monthStart = `${today.slice(0, 8)}01`;
  const out = {};
  (costs || []).forEach((c) => {
    if (c.cost_type === 'deposit') return;
    const factor = PER_MONTH[String(c.frequency || '').toLowerCase()];
    if (!factor) return;
    const from = String(c.effective_from || '').slice(0, 10);
    const to = String(c.effective_to || '').slice(0, 10);
    if (from && from > today) return;
    if (to && to < monthStart) return;
    const amt = Math.abs(costAmount(c.amount)) * factor;
    if (!amt) return;
    const ccy = (c.currency || 'GBP').toUpperCase();
    out[ccy] = (out[ccy] || 0) + amt;
  });
  return out;
}
